import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import {
  addStateCameraType1,
  editStateCameraType1,
  rowEditCameraType1,
  cameraType1Fetching,
} from "../atoms/atoms";
import useAddCameraType1 from "../hooks/useAddCameraType1";
import { ICameraType1 } from "../types";
import CameraType1Details from "./CameraType1Details";
import useEditCameraType1 from "../hooks/useEditCameraType1";
import SubmitHookForm from "components/SubmitHookForm";

export const CameraType1Form = (props: {
  setRecent: React.Dispatch<React.SetStateAction<string>>;
}) => {
  // Drilled Props
  const { setRecent } = props;
  // Atom States
  const [isAddState, setIsAddState] = useRecoilState(addStateCameraType1);
  const [actionEditState, setActionEditState] =
    useRecoilState(editStateCameraType1);
  const [editData, setEditData] = useRecoilState(rowEditCameraType1);
  const setIsFetching = useSetRecoilState(cameraType1Fetching);

  // Local States
  const [newData, setNewData] = useState<ICameraType1>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Hook Functions
  const updateData = useEditCameraType1();
  const addData = useAddCameraType1();

  // Form Functions
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const value = {
      ...data,
      cameraType1Name: data.cameraType1Name.trim(),
    };
    setNewData(value);
    setSaveModal(true);
    setChanges(false);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setActionEditState(false);
  };

  const handleSave = () => {
    if (newData) {
      if (actionEditState) {
        updateData(newData);
      } else {
        addData(newData);
      }
    }
    setRecent("");
    setIsFetching(true);
    setIsSaving(false);
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
  };

  return (
    <>
      <div className="p-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Camera Type 1" : "Edit Camera Type 1"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <CameraType1Details data={editData} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Camera Type 1" : "Update Camera Type 1"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
            setChanges(true);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default CameraType1Form;
